<template>
	<div class="synced-group-users-action-type" v-if="isServiceGroupSupported && !isLoading">
		<div class="section-title">Syncing options</div>
		<AppRadioButton v-model="syncWhat" :choices="syncWhatTypes">
			<template v-slot:label="{ item }">
				<div>
					<div class="label">
						{{ item.title }}<span class="ml-1 sub-label">{{ item.subTitle }}</span>
					</div>
					<div v-if="isSpecificGroups(item)">
						<v-checkbox
							v-model="subGroupsSelected[subGroup.id]"
							v-for="(subGroup, index) in matchingGroups"
							:key="index"
							class="mt-n1"
							:ripple="false"
							hide-details
						>
							<template v-slot:label>
								<div class="label">
									{{ subGroup.name }} <span class="sub-label">({{ subGroup.members.length || 0 }} users) </span>
								</div>
							</template>
						</v-checkbox>
					</div>
				</div>
			</template>
		</AppRadioButton>
		<div v-if="showOnDelete" class="on-delete-type">
			<div class="section-title">What do you want to do when a {{ serviceGroupNoun }} is deleted</div>
			<AppRadioButton v-model="onDeleteAction" :choices="onDeleteActionTypes">
				<template v-slot:label="{ item }">
					<div class="label">
						{{ item.title }}<span class="ml-1 sub-label">{{ item.subTitle }}</span>
					</div>
				</template>
			</AppRadioButton>
		</div>
	</div>
</template>

<script>
import { mapActions } from 'vuex';

import { setUserMatchingData } from '@/store/modules/credentials/actions';
import { userMatchingData as credentialsUserMatchingData } from '@/store/modules/credentials/getters';
import { mapGettersShorthand } from '@/store/utils/mappers';
import { SYNC_GROUP_ON_DELETE_TYPE, WHAT_TO_SYNC_TYPES } from '@/utils/groups-const';
import GroupsService from '@/utils/groups-service';
import { pluralize } from '@/utils/language-service';

export default {
	name: 'ChooseWhatAndHowToSync',
	components: {},
	props: {
		group: { type: Object, default: null },
		service: { type: Object },
		userMatchingData: { type: Object, default: null },
	},
	methods: {
		...mapActions([setUserMatchingData.fullPath]),
		isSpecificGroups(item) {
			return (
				item.value === WHAT_TO_SYNC_TYPES.SPECIFIC_GROUPS_AND_ONLY_THEIR_USERS &&
				this.syncWhat === WHAT_TO_SYNC_TYPES.SPECIFIC_GROUPS_AND_ONLY_THEIR_USERS
			);
		},
	},
	computed: {
		...mapGettersShorthand({ credentialsUserMatchingData }),
		matchingUsers() {
			if (this.group?.synced?.credentials) {
				return this.credentialsUserMatchingData(this.group.synced?.credentials).users || [];
			}
			return this.userMatchingData?.users || [];
		},
		matchingGroups() {
			if (this.group?.synced?.credentials) {
				return this.credentialsUserMatchingData(this.group.synced?.credentials).groups || [];
			}
			return this.userMatchingData?.groups || [];
		},
		serviceName() {
			return this.service?.service_name;
		},
		serviceGroupNoun() {
			return GroupsService.serviceGroupNoun(this.service);
		},
		isServiceGroupSupported() {
			return this.service?.group_metadata?.is_supported || this.subgroupsCount > 0;
		},
		pluralServiceGroupNoun() {
			return pluralize(this.serviceGroupNoun, 2); //more then one
		},
		subgroupsCount() {
			return this.matchingGroups.length;
		},
		showOnDelete() {
			return this.syncWhat !== WHAT_TO_SYNC_TYPES.ONLY_USERS;
		},
		syncWhatTypes() {
			const options = [
				{
					title: `Sync all ${this.serviceName} users as a group.`,
					subTitle: 'Synced users will be added to your workspace automatically',
					value: WHAT_TO_SYNC_TYPES.ONLY_USERS,
					disabled: false,
				},
				{
					title: `Sync all ${this.serviceName} users as a group, with ${this.serviceName} ${this.pluralServiceGroupNoun} as subgroups.`,
					subTitle: `(you have ${pluralize(this.serviceGroupNoun, this.subgroupsCount, true)})`,
					value: WHAT_TO_SYNC_TYPES.USERS_AND_GROUPS,
					disabled: !this.isServiceGroupSupported,
				},
				{
					title: `Sync only the users from the selected ${this.serviceName} ${this.pluralServiceGroupNoun} as subgroups.`,
					subTitle: '',
					value: WHAT_TO_SYNC_TYPES.SPECIFIC_GROUPS_AND_ONLY_THEIR_USERS,
					disabled: !this.isServiceGroupSupported || this.matchingGroups.length === 0,
				},
			];
			return options.filter((x) => !x.disabled);
		},
		onDeleteActionTypes() {
			return [
				{
					title: `Turn the group into a regular group.`,
					subTitle: '(Recommended)',
					value: SYNC_GROUP_ON_DELETE_TYPE.TURN_MANUAL,
				},
				{
					title: `Delete the group in Ply.`,
					subTitle: '',
					value: SYNC_GROUP_ON_DELETE_TYPE.DELETE,
				},
			];
		},
	},
	async mounted() {
		try {
			if (this.group) {
				if (this.group.synced?.credentials) {
					await this[setUserMatchingData.fullPath]({ credentialsId: this.group.synced?.credentials });
				}
				this.syncWhat = this.group.synced.what_to_sync_type;
				this.onDeleteAction = this.group.synced.on_delete_group_action_type;
				this.subGroupsSelected = this.group.synced.subgroups_to_sync.reduce((res, group) => ({ ...res, [group]: true }), {});
			}
		} catch (err) {
			console.error(err);
		} finally {
			this.isLoading = false;
		}
	},
	watch: {
		syncWhat: {
			handler(val) {
				val && this.$emit('selected-sync-what-type', val);
				if (val !== WHAT_TO_SYNC_TYPES.SPECIFIC_GROUPS_AND_ONLY_THEIR_USERS) {
					this.subGroupsSelected = {};
				}
			},
		},
		onDeleteAction: {
			handler(val) {
				val && this.$emit('selected-on-delete-type', val);
			},
		},
		subGroupsSelected: {
			handler(val) {
				val &&
					this.$emit(
						'selected-specific-groups',
						Object.entries(val).reduce((res, [key, value]) => (value ? [...res, key] : res), [])
					);
			},
			deep: true,
		},
	},
	data() {
		return {
			syncWhat: WHAT_TO_SYNC_TYPES.USERS_AND_GROUPS,
			onDeleteAction: SYNC_GROUP_ON_DELETE_TYPE.TURN_MANUAL,
			subGroupsSelected: {},
			isLoading: true,
		};
	},
};
</script>

<style scoped lang="scss">
.synced-group-users-action-type {
	.sub-label {
		font-size: 11px;
		color: var(--primary-font-color);
		opacity: 0.5;
	}
	.label {
		font-size: 14px;
		color: var(--primary-font-color);
	}
	.section-title {
		@extend .label;
		font-weight: medium;
	}
}
</style>
